import { useNavigate } from 'react-router-dom'
import { Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { BG_COLORS } from '@/utils/advisorChartUtils'
import type { TeamCharterStatus } from '../../redux/types/charterTypes'
import ROUTES from '@/routes/routes'

const STAGE_LABELS: Record<string, string> = {
  opening: 'Opening',
  easy_wins: 'Easy Wins',
  conflicts: 'Conflict Resolution',
  group_sections: 'Group Sections',
  gap_review: 'Gap Review',
  ratification: 'Ratification',
}

const STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  not_started: { label: 'Not Started', cls: 'bg-gray-100 text-gray-600' },
  in_progress: { label: 'In Progress', cls: 'bg-blue-100 text-blue-700' },
  completed: { label: 'Completed', cls: 'bg-emerald-100 text-emerald-700' },
  locked: { label: 'Ratified', cls: 'bg-indigo-100 text-indigo-700' },
  renegotiating: { label: 'Amending', cls: 'bg-amber-100 text-amber-700' },
}

interface GroupMonitoringCardProps {
  teamStatus: TeamCharterStatus
  roomId: string
  inviteCode: string
  colorIndex: number
}

export function GroupMonitoringCard({
  teamStatus,
  roomId,
  inviteCode,
  colorIndex,
}: GroupMonitoringCardProps) {
  const navigate = useNavigate()
  const bgColor = BG_COLORS[colorIndex % BG_COLORS.length]

  const session = teamStatus.session
  const status = session?.status ?? 'not_started'
  const stage = session?.current_group_stage
  const memberCount = teamStatus.members.length
  const { label: statusLabel, cls: statusCls } =
    STATUS_BADGE[status] ?? STATUS_BADGE.not_started

  const handleViewChat = () => {
    const url =
      ROUTES.PROFESSOR.ROOM_CHARTER_GROUP_MONITOR(roomId, String(teamStatus.team_id)) +
      `?inviteCode=${inviteCode}&teamName=${encodeURIComponent(teamStatus.team_name)}`
    navigate(url)
  }

  return (
    <div
      className={`flex flex-col min-h-[300px] ${bgColor} rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] border border-white/10 relative group`}
    >
      <div className="absolute inset-0 bg-[url('/images/create-room-bg.png')] bg-cover bg-center opacity-40 transition-opacity group-hover:opacity-60" />

      {/* Status badge */}
      <div className='absolute z-10 top-3 left-3'>
        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${statusCls}`}>
          {statusLabel}
        </span>
      </div>

      <div className='z-[1] flex flex-col h-full p-4 pt-14'>
        {/* Team name — matches AdvisorChatCard font */}
        <div className='text-center mb-2'>
          <p className='text-sm font-bold text-white leading-tight'>
            {teamStatus.team_name}
          </p>
          <p className='text-[11px] text-white/60 mt-0.5'>as</p>
          <div className='flex items-center justify-center gap-1'>
            <Users className='w-3 h-3 text-white' />
            <p className='text-sm font-semibold text-white leading-tight'>
              {memberCount} members
            </p>
          </div>
        </div>

        {/* Stage info */}
        <div className='flex-1 flex flex-col items-center justify-center gap-3 py-2'>
          {stage ? (
            <div className='text-center'>
              <p className='text-[10px] text-white/60 uppercase tracking-wider mb-1'>
                Current Stage
              </p>
              <p className='text-sm font-bold text-white'>
                {STAGE_LABELS[stage] ?? stage}
              </p>
            </div>
          ) : (
            <p className='text-sm text-white/60 italic'>Session not started</p>
          )}

          {session && Object.keys(session.ratification_votes).length > 0 && (
            <div className='text-center'>
              <p className='text-[10px] text-white/60 uppercase tracking-wider mb-1'>
                Ratification
              </p>
              <p className='text-sm font-bold text-white'>
                {Object.values(session.ratification_votes).filter((v) => v.vote).length}/
                {memberCount} approved
              </p>
            </div>
          )}
        </div>

        {/* View Chat button — matches AdvisorChatCard style */}
        <Button
          className='w-full max-w-[200px] px-6 py-1.5 h-9 text-sm font-medium rounded-md transition-all mx-auto bg-white text-blue-800 hover:bg-blue-50 shadow-md hover:shadow-lg'
          onClick={handleViewChat}
        >
          View Chat
        </Button>
      </div>
    </div>
  )
}
